import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { recommendationAPI, Recommendation, Strategy } from '../services/api';

export interface StockRecommendation {
  id: string;
  strategy_id: string;
  stock_code: string;
  stock_name: string;
  score: number;
  rank: number;
  factors: Record<string, number>;
  created_at: string;
}

interface RecommendationsState {
  data: StockRecommendation[];
  strategies: Strategy[];
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
  total: number;
}

const initialState: RecommendationsState = {
  data: [],
  strategies: [],
  loading: false,
  error: null,
  lastUpdated: null,
  total: 0,
};

export const fetchRecommendations = createAsyncThunk(
  'recommendations/fetch',
  async (params: { strategyId?: string; topN?: number }) => {
    const response = await recommendationAPI.getRecommendationHistory(params);
    return response.data;
  }
);

export const fetchStrategies = createAsyncThunk(
  'recommendations/fetchStrategies',
  async () => {
    const response = await recommendationAPI.getStrategies();
    return response.data;
  }
);

export const generateRecommendations = createAsyncThunk(
  'recommendations/generate',
  async (params: { strategyId: string; data?: any }) => {
    const response = await recommendationAPI.generateRecommendations(params.strategyId, params.data);
    return response.data;
  }
);

const recommendationsSlice = createSlice({
  name: 'recommendations',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateRecommendation: (state, action) => {
      const { code, updates } = action.payload;
      const index = state.data.findIndex(item => item.code === code);
      if (index !== -1) {
        state.data[index] = { ...state.data[index], ...updates };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecommendations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecommendations.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data || [];
        state.total = action.payload.total || 0;
        state.lastUpdated = action.payload.timestamp || new Date().toISOString();
      })
      .addCase(fetchRecommendations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取推荐数据失败';
      })
      .addCase(fetchStrategies.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStrategies.fulfilled, (state, action) => {
        state.loading = false;
        state.strategies = action.payload.data || [];
      })
      .addCase(fetchStrategies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取策略列表失败';
      })
      .addCase(generateRecommendations.pending, (state) => {
        state.loading = true;
      })
      .addCase(generateRecommendations.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data || [];
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(generateRecommendations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '生成推荐失败';
      });
  },
});

export const { clearError, updateRecommendation } = recommendationsSlice.actions;
export default recommendationsSlice.reducer;
